import { FlaskConical } from 'lucide-react';

import {
  DEFAULT_PERSONA_CHAT_MODEL,
  PERSONA_CHAT_MODELS,
} from '@repo/contracts/persona-models';

import { useIsAdmin } from '../admin/admin-route';
import { useResearchRoomStore } from '../store/research-room-store';
import { PERSONA_MODEL_PICKER_ENABLED } from './persona-model-picker-enabled';

/**
 * Which model the personas answer with — for the Floto team only, to compare
 * models on real sessions before one is picked for launch.
 *
 * Applies from the next question on, so the same question can be asked again
 * under another model and the two sets of answers read one above the other;
 * each answer is labelled with the model that wrote it. Remembered in this
 * browser. The API refuses a pick from anyone who is not an admin, so hiding
 * it here is only about not showing customers an internal switch.
 */
export function PersonaModelPicker() {
  const isAdmin = useIsAdmin();
  const picked = useResearchRoomStore((s) => s.personaModel);
  const setPicked = useResearchRoomStore((s) => s.setPersonaModel);

  if (!PERSONA_MODEL_PICKER_ENABLED || !isAdmin) return null;
  const current = picked ?? DEFAULT_PERSONA_CHAT_MODEL;

  return (
    <div
      aria-label="Persona model (internal)"
      className="mt-2 flex flex-wrap items-center gap-1.5"
      role="radiogroup"
    >
      <span className="flex items-center gap-1 text-tag text-text-muted">
        <FlaskConical aria-hidden="true" className="size-3" />
        Model · internal
      </span>
      {PERSONA_CHAT_MODELS.map((model) => {
        const on = model.id === current;
        return (
          <button
            aria-checked={on}
            className={[
              'h-6 cursor-pointer rounded-pill px-2.5 text-tag font-semibold transition',
              on
                ? 'bg-ink text-white'
                : 'bg-warm text-text-secondary hover:text-text-primary',
            ].join(' ')}
            key={model.id}
            onClick={() => {
              setPicked(
                model.id === DEFAULT_PERSONA_CHAT_MODEL ? null : model.id,
              );
            }}
            role="radio"
            title={model.id}
            type="button"
          >
            {model.label}
          </button>
        );
      })}
    </div>
  );
}
